import { Loader } from "@radianse/components";
import { UserContext } from "../../context";
import ErrorMsg from "../../common/ErrorMsg";
import { identity_instance } from "../../apiConfig";
import { DateTime } from "luxon";
import DocumentIframe from "../../common/DocumentIframe";
import SignatureInput from "../../common/SignatureInput";
import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  useDisclosure,
  VStack,
  Flex,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  Input,
  Heading,
  FormControl,
  FormLabel,
  ModalHeader,
} from "@chakra-ui/react";
import { useMutation } from "react-query";
import { Controller, useForm } from "react-hook-form";
import { useApiUser } from "../../context/ApiAuthContext";
import { PencilIcon } from "@heroicons/react/outline";
import { useTranslation, Trans } from "react-i18next";

const TemplateRequirement = ({ document, onComplete, onError, onCancel }) => {
  const { t } = useTranslation();
  const { userData } = React.useContext(UserContext);
  const { user, event } = userData;
  const authUser = useApiUser();
  const iframeRef = useRef();
  const formRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const requiredData = document?.metaData?.RequiredData?.data;
  const documentSigningUrl = document?.metaData?.UrlDocumentSigning?.data;
  const documentType = document?.metaData?.DocumentType?.data;
  const documentRevisionId = document?.metaData?.DocumentRevisionId?.data;

  let subCategory = "Events";
  if (userData?.event?.children && userData?.event?.children.length > 0) {
    subCategory = "ElementTree";
  }
  const eventName = t(`${subCategory}.${userData?.event?.name}`, {
    ns: subCategory,
  });
  const eventTime = DateTime.fromISO(userData?.lock?.startTime).toFormat("t");

  // define template suffixes to local variable sources i.e. member -> memberFirstName
  const templateSources = useMemo(
    () => ({
      member: user,
      event: event,
    }),
    [user, event]
  );

  // map template expressions to template source data
  const mapExpressionToValue = (expressionName, templateSources) => {
    const expr = expressionName.toLowerCase();

    for (const [prefix, sourceObj] of Object.entries(templateSources)) {
      const prefixLower = prefix.toLowerCase();

      if (expr.startsWith(prefixLower)) {
        const suffix = expr.slice(prefixLower.length);

        const keysMap = Object.keys(sourceObj).reduce((map, key) => {
          map[key.toLowerCase()] = key;
          return map;
        }, {});

        if (keysMap[suffix]) {
          return sourceObj[keysMap[suffix]];
        }
      }
    }

    return null;
  };

  const defaultValues = {
    TenantIdOfSigning: authUser.tenantId,
    documentRevisionId: documentRevisionId,
    createDate: new Date().toISOString(),
    documentData: undefined,
    templateData: requiredData.reduce((acc, cur) => {
      if (cur.Type === "CurrentDateTime") {
        acc[cur.ExpressionName] = new Date().toLocaleDateString();
        return acc;
      }

      if (cur.Type === "Signature") {
        acc[cur.ExpressionName] = null;
        return acc;
      }

      acc[cur.ExpressionName] = mapExpressionToValue(
        cur.ExpressionName,
        templateSources
      );
      return acc;
    }, {}),
  };

  const sortExpressions = (a, b) => {
    const aHasSignature =
      a?.DisplayName.toLowerCase().includes("signature") || false;
    const bHasSignature =
      b?.DisplayName.toLowerCase().includes("signature") || false;

    // If one has signature and the other doesn't, signature goes last
    if (aHasSignature && !bHasSignature) return 1;
    if (!aHasSignature && bHasSignature) return -1;

    // Otherwise sort alphabetically by DisplayName
    return (a?.DisplayName || "").localeCompare(b?.DisplayName || "");
  };

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { mutate, isLoading, isError, error } = useMutation(
    (data) => identity_instance.put(documentSigningUrl, data),
    {
      onSuccess: () => {
        onComplete();
      },
      onError: (res) => onError(res.response.data),
    }
  );

  const onFormSubmit = (data) => {
    mutate(data);
  };

  const onSign = () => {
    if (documentType === "QuestionWeighted") {
      // QuestionWeighted documents require additional document content to be uploaded when signing
      // this postMessage submits the form in the iframe which will validate and post back using the "message" event with a type of "post"
      iframeRef.current?.contentWindow?.postMessage("submit", "*");
    } else if (requiredData) {
      onOpen();
    }
  };
  const onIframeMsg = (event) => {
    const msg = event?.data;
    if (msg.type === "post") {
      setValue("documentData", {
        data: `data:application/json;base64,${btoa(JSON.stringify(msg.data))}`,
        mediaType: "application/json",
      });

      if (requiredData) {
        onOpen();
      } else {
        formRef.current.requestSubmit();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", onIframeMsg);
    return () => {
      window.removeEventListener("message", onIframeMsg);
    };
  }, []);

  if (isLoading) {
    return (
      <Flex w={"full"} h={"full"}>
        <Loader text={t("loader.signing", "Signing...")} />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Flex w={"full"} h={"full"} direction={"column"} alignItems={"center"}>
        <ErrorMsg
          message={
            error?.detail ||
            t(
              "error.document_sign",
              "Error signing required document, please see a staff member for assistance."
            )
          }
        >
          <Button
            variant={"kioskPrimary"}
            onClick={onCancel}
            data-testid={"error-retry"}
          >
            <Trans i18nKey={"actions.retry"}>Retry</Trans>
          </Button>
        </ErrorMsg>
      </Flex>
    );
  }
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={"xl"} isCentered>
        <ModalOverlay />
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          ref={formRef}
          data-testid={"template-requirement-form"}
        >
          <ModalContent className={"content"}>
            <ModalHeader color={"black"}>
              <Trans i18nKey={"please_complete_form"}>
                Please complete the following form
              </Trans>
            </ModalHeader>
            <ModalBody>
              <Input
                type={"hidden"}
                {...register("TenantIdOfSigning")}
                data-testid={"TenantIdOfSigning"}
              />
              <Input
                type={"hidden"}
                {...register("documentRevisionId")}
                data-testid={"documentRevisionId"}
              />
              <Input
                type={"hidden"}
                {...register("createDate")}
                data-testid={"createDate"}
              />
              <Input
                type={"hidden"}
                {...register("documentData")}
                data-testid={"documentData"}
              />
              {requiredData
                .sort(sortExpressions)
                .map(({ Type, ExpressionName, DisplayName }) => {
                  if (Type === "Signature") {
                    return (
                      <Controller
                        key={ExpressionName}
                        control={control}
                        name={`templateData.${ExpressionName}`}
                        rules={{
                          required: true,
                        }}
                        defaultValue={`defaultValues.templateData.${ExpressionName}`}
                        render={({ onChange, onBlur, value }) => (
                          <SignatureInput
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value}
                            data-testid={"signature-input"}
                            error={errors?.templateData?.[ExpressionName]}
                          />
                        )}
                      />
                    );
                  } else {
                    return (
                      <FormControl key={ExpressionName} mb={2}>
                        <FormLabel
                          htmlFor={`templateData.${ExpressionName}`}
                          color={"black"}
                          display={
                            defaultValues?.templateData?.[ExpressionName]
                              ? "none"
                              : "block"
                          }
                        >
                          {DisplayName}
                        </FormLabel>
                        <Input
                          name={`templateData.${ExpressionName}`}
                          data-testid={`${ExpressionName}-input`}
                          ref={register}
                          required
                          placeholder={DisplayName}
                          type={
                            defaultValues?.templateData?.[ExpressionName]
                              ? "hidden"
                              : "text"
                          }
                        />
                      </FormControl>
                    );
                  }
                })}
            </ModalBody>
            <ModalFooter>
              <Button
                variant={"kioskPrimary"}
                type={"submit"}
                data-testid={"sign"}
              >
                Sign
              </Button>
            </ModalFooter>
          </ModalContent>
        </form>
      </Modal>

      <VStack
        direction={"column"}
        alignItems={"start"}
        flex={1}
        minHeight={0}
        h={"full"}
        spacing={5}
        pr={4}
      >
        <Heading
          as={"h2"}
          color="brand.secondary.500"
          fontSize="2xl"
          textAlign={"left"}
          fontWeight={"bold"}
        >
          <Trans
            i18nKey={"new_appointment_event_time"}
            values={{ eventName: eventName, dateTime: eventTime }}
          >
            New Appointment: {userData?.event?.name} @{" "}
            {DateTime.fromISO(userData?.lock?.startTime).toFormat("t")}
          </Trans>
        </Heading>
        <Heading as="h3" fontSize="xl" textAlign={"left"} fontWeight={"bold"}>
          <Trans i18nKey={"please_review_document"}>
            Please review the document below and click <i>&quot;Sign&quot;</i>{" "}
            to agree and continue.
          </Trans>
        </Heading>

        <DocumentIframe
          iframeRef={iframeRef}
          document={document}
          onNext={onSign}
        />
        <Flex justifyContent="flex-end" pb={10} flex={1} w={"full"}>
          <Button
            size={"lg"}
            onClick={onCancel}
            mr={5}
            data-testid={"document-cancel-button"}
          >
            <Trans i18nKey={"actions.cancel"}>Cancel</Trans>
          </Button>

          <Button
            size={"lg"}
            onClick={onSign}
            leftIcon={<PencilIcon height={12} />}
            variant="kioskPrimary"
            isLoading={isLoading}
            loadingText={t("loader.signing", "Signing...")}
            disabled={isLoading}
            data-testid={"document-sign-button"}
          >
            <Trans i18nKey={"actions.sign"}>Sign</Trans>
          </Button>
        </Flex>
      </VStack>
    </>
  );
};

export default TemplateRequirement;
